import {
  campaignAddress,
  giftToFor,
  normalizeShortId,
  officeOrgNounFor,
  orgWordFor,
} from '@app/shared'
import {
  appHostFor,
  DEFAULT_PRODUCT,
  hostFor,
  isAppHost,
  isPlatformHost,
  PLATFORM_HOST,
  PLATFORM_NAME,
  PRODUCT_DIRECTORIES,
  type Product,
  productFor,
  productName,
} from '@app/shared/brand'
import { useEffect, useState } from 'react'
import { kindForProduct } from './org'

/**
 * The public surface's route map — every signed-out path, in one block.
 *
 * ## Every org page hangs off the org's own id
 *
 * `/{shortId}/zmanim`, `/{shortId}/donate`, `/{shortId}` — the id is the FIRST
 * segment, and that is the load-bearing decision here rather than a tidiness
 * one. It makes an org's pages a subtree with a single mount point, so a custom
 * domain becomes a prefix substitution: `shul.com/zmanim` can serve exactly what
 * `shulbase.com/AB3K9F2Q/zmanim` serves, with the same routes underneath. The
 * alternative — org pages scattered under platform paths, keyed by a query
 * string — would need a second routing scheme for custom domains, kept in step
 * with this one by hand, forever.
 *
 * Two rules follow, and both are easy to break by accident:
 *
 *  1. **No org page may live outside the prefix.** One that does is a page that
 *     cannot be served on the org's own domain.
 *  2. **No link inside an org's site may hard-code the id.** Build them with
 *     `orgHref`, which resolves against the mount point — on a custom domain the
 *     prefix is empty, and a link carrying the id would leak the platform's
 *     address into a page that is supposed to have stopped mentioning it.
 *
 * ## Why an id can never be mistaken for a platform path
 *
 * Every short id contains at least one letter AND one digit, so it cannot spell
 * a word (see `newShortId`). `normalizeShortId` rejects anything else, which
 * means the first segment can be handed to it directly: `/login` and
 * `/preferences` fail to normalize and fall through to the platform's own
 * routes, with no reserved-word list to maintain and no database round trip.
 */

/** What a public address resolves to. */
export type PublicRoute =
  | { kind: 'home' }
  /** Where somebody agrees what a connected agent may reach. */
  | { kind: 'consent' }
  /** Not a route of its own — the sign-in screen is a state, see `main.tsx`. */
  | { kind: 'login' }
  /** The directory of congregations publishing here. */
  | { kind: 'shuls' }
  /** The directory of charities that chose to publish here. */
  | { kind: 'orgs' }
  /**
   * The demo door — a standing address a stranger can be sent, which signs them
   * into the demo congregation. An address rather than a button so it can be
   * put in a message, a talk or a footer and still work.
   */
  | { kind: 'demo' }
  /**
   * An org page. `orgId` is the canonical (uppercase) short id.
   *
   * `page` is `''` at the org's own root (`/{orgId}`, or `/` on its domain) —
   * its home page, which is a page of its own rather than one of the named
   * ones, so it is the one address every org answers the same way.
   *
   * A campaign year and a saved board are addressable below their page. Each
   * remains the page it belongs to rather than becoming a second page kind.
   */
  | {
      kind: 'org'
      orgId: string
      page: OrgPage | ''
      campaignId?: string
      boardId?: string
    }
  /**
   * The exchange that carries a session between our sites.
   *
   * It answers on the PLATFORM's address and nowhere else: one place converts
   * a session into one somewhere else, so there is one thing to reason about
   * rather than a product deciding for another product.
   */
  | {
      kind: 'sso'
      to: string
      next: string
      /** The caller already has a session there and came only to tell us. */
      have: boolean
    }
  /**
   * A book somebody published a link to.
   *
   * Deliberately NOT under an org's prefix, and it is the first public page
   * that isn't. A book belongs to a PERSON, in their own space, and an org has
   * no part in it — mounting it under `/{shortId}` would say a congregation
   * publishes it, and on a custom domain would serve somebody's private
   * manuscript from a shul's address.
   */
  | { kind: 'read'; token: string }
  | { kind: 'notFound' }

/** The first path segment, and the path, the demo door answers at. */
const DEMO_SEGMENT = 'demo'
export const DEMO_PATH = `/${DEMO_SEGMENT}`

/** The pages an org publishes, all of them under its own root. */
export type OrgPage =
  | 'zmanim'
  | 'donate'
  | 'contact'
  | 'preferences'
  | 'pay'
  | 'gift'
  | 'autopay'
  | 'stop-gift'
  | 'confirm-gift'
  | 'confirm-membership'
  | 'change-card'
  | 'board'

const ORG_PAGES: Record<string, OrgPage> = {
  zmanim: 'zmanim',
  donate: 'donate',
  contact: 'contact',
  preferences: 'preferences',
  pay: 'pay',
  gift: 'gift',
  autopay: 'autopay',
  'stop-gift': 'stop-gift',
  'confirm-gift': 'confirm-gift',
  'confirm-membership': 'confirm-membership',
  'change-card': 'change-card',
  board: 'board',
}

export function resolvePublicRoute(url: URL): PublicRoute {
  const segments = url.pathname.split('/').filter(Boolean)

  const orgId = segments[0] ? normalizeShortId(segments[0]) : null
  if (orgId) {
    if (segments.length === 1) return { kind: 'org', orgId, page: '' }
    const page = ORG_PAGES[segments[1].toLowerCase()]
    // An org id followed by something that is not one of its pages is a bad
    // address, not a reason to show a different org's page.
    if (page && segments.length === 2) return { kind: 'org', orgId, page }
    /*
     * Addressable things below a page use the same short-id normalization as
     * the org, so casing and hyphens in a copied address do not change it and a
     * malformed id falls through rather than reaching the database.
     */
    if (page === 'donate' && segments.length === 3) {
      // A campaign answers to its year's id, its own id, or an alias.
      const named = campaignAddress(segments[2])
      if (named) {
        const campaignId = 'shortId' in named ? named.shortId : named.alias
        return { kind: 'org', orgId, page, campaignId }
      }
    }
    if (page === 'board' && segments.length === 3) {
      const boardId = normalizeShortId(segments[2])
      if (boardId) return { kind: 'org', orgId, page, boardId }
    }
    return { kind: 'home' }
  }

  if (segments[0] === 'login') return { kind: 'login' }
  if (
    segments[0] === 'oauth' &&
    segments[1] === 'consent' &&
    segments.length === 2
  )
    return { kind: 'consent' }
  const requestedDirectory = Object.values(PRODUCT_DIRECTORIES).find(
    (directory) => directory?.path === segments[0],
  )
  if (requestedDirectory) {
    const site = resolveSite(url)
    const ownDirectory =
      site === 'platform' || site === 'org' ? null : PRODUCT_DIRECTORIES[site]
    if (ownDirectory !== requestedDirectory) return { kind: 'notFound' }
    return requestedDirectory.kind === 'shul'
      ? { kind: 'shuls' }
      : { kind: 'orgs' }
  }
  if (segments[0] === DEMO_SEGMENT) return { kind: 'demo' }
  if (segments[0] === 'sso') {
    const to = url.searchParams.get('to') ?? ''
    const next = url.searchParams.get('next') ?? '/'
    return {
      kind: 'sso',
      to,
      next: next.startsWith('/') ? next : '/',
      have: url.searchParams.get('have') === '1',
    }
  }

  /*
   * A shared book. The token is the whole authorization, so it is passed
   * through as written and answered by the server — there is nothing to
   * normalize here, and a bad one is refused there rather than guessed at.
   */
  if (segments[0] === 'read' && segments[1]) {
    const product = productFor(url.host)
    return product &&
      kindForProduct(product) === 'personal' &&
      !isAppHost(url.host)
      ? { kind: 'read', token: segments[1] }
      : { kind: 'notFound' }
  }

  /*
   * Legacy platform-scoped preferences. Every unsubscribe link already sent
   * points here, and the token carries the space on its own, so this keeps
   * working rather than 404-ing mail that is already in people's inboxes. New
   * links are org-scoped; this stays until none are in circulation.
   */
  if (segments[0] === 'preferences')
    return { kind: 'org', orgId: '', page: 'preferences' }

  /*
   * Anything else is the front door, deliberately NOT a 404. A signed-out
   * visitor following a deep link into the back office has to land somewhere
   * they can sign in from; 404-ing them breaks the one flow that makes such a
   * link worth sending. On a product that somewhere is its app (`faceFor`), and
   * where there is one host it is the front door itself. Not-found belongs to
   * an org id that does not resolve, which is a different failure and is
   * decided against the database.
   */
  return { kind: 'home' }
}

/**
 * Which face of a product an address belongs to, or null where both answer it.
 *
 * What a congregation publishes, the directory and a shared book are the
 * public site's: they are for people who may have no account at all. So is an
 * address SHAPED like one of them that does not resolve — a congregation's id
 * with a page it does not have is a bad public address, and is answered as one
 * where it was asked rather than with a sign-in.
 *
 * Everything else a route map calls `home` below the root is a path into the
 * back office, the app's — as are the sign-in, a sign-in link, the demo door
 * and a consent, because each of them is about a session and sessions live
 * there. The root belongs to both: it is the front door on one host and the
 * back office's first screen on the other.
 */
export function faceFor(route: PublicRoute, pathname: string): Face | null {
  switch (route.kind) {
    case 'org':
    case 'shuls':
    case 'orgs':
    case 'read':
      return 'public'
    case 'notFound':
      return null
    case 'login':
    case 'demo':
    case 'consent':
      return 'app'
    case 'sso':
      return null
    case 'home': {
      if (pathname === '/') return null
      const first = pathname.split('/')[1] ?? ''
      return normalizeShortId(first) || first === 'read' ? 'public' : 'app'
    }
  }
}

/** Resolves the address the browser is currently at. */
export function currentPublicRoute(): PublicRoute {
  return resolvePublicRoute(new URL(window.location.href))
}

/**
 * A link to one of an org's pages, built from its mount point.
 *
 * On the platform the mount point is `/{shortId}`; on an org's own domain it is
 * empty and the same call yields `/zmanim`. Every link inside an org's site goes
 * through here so that switching between the two is a change to this function
 * rather than to every page that happens to render an anchor.
 */
export function orgHref(
  orgId: string,
  page: OrgPage | '',
  /** A campaign year or saved board, where the page addresses one. */
  childId?: string,
): string {
  const base = orgId ? `/${orgId}` : ''
  if (!page) return base || '/'
  return childId ? `${base}/${page}/${childId}` : `${base}/${page}`
}

/**
 * Whose site this is — which is a question about the HOST, not the path.
 *
 * A product answers on its own host; the platform answers on its own; anything
 * else is a congregation serving its site on a domain of its own, which is what
 * `org` means here. The three are mutually exclusive, so everything conditional
 * on "whose site is this" asks one question and gets one answer.
 */
export type Site = Product | 'platform' | 'org'

/**
 * The site a URL belongs to.
 *
 * The bare `localhost` is the institutional product, so a developer who starts
 * the server and opens it lands where they expect. Every other site is reached
 * at its slug's subdomain of localhost, and the platform at `platform.localhost`,
 * which browsers resolve without a hosts file (see `productFor` /
 * `isPlatformHost`).
 */
export function resolveSite(url: URL): Site {
  const host = url.hostname
  const product = productFor(host)
  if (product) return product
  if (isPlatformHost(host)) return 'platform'
  if (host === 'localhost' || host === '127.0.0.1') return DEFAULT_PRODUCT
  return 'org'
}

/** The site the browser is currently on. */
export function currentSite(): Site {
  return resolveSite(new URL(window.location.href))
}

/**
 * The product being served here, or null where this is not a product's site.
 *
 * Chrome asks this rather than being told which brand to wear: the address has
 * already settled which front door was served, and a page that names its own
 * product is a second answer that can disagree with the first.
 */
export function currentProduct(): Product | null {
  const site = currentSite()
  return site === 'platform' || site === 'org' ? null : site
}

/**
 * Which products' org sites carry the luach — the schedule page, and what is
 * next at the shul leading the front door.
 *
 * A product's answer, because an org's product follows from its kind: a
 * charity has no davening to publish, so its site leads with giving instead.
 * Declared once so the nav, the route and the front door cannot disagree.
 */
const CARRIES_TIMES: Record<Product, boolean> = {
  [DEFAULT_PRODUCT]: true,
  terumah: false,
  studio: false,
}

/** The products whose orgs carry the luach — a shul's, and only a shul's. */
export function productsCarryingTimes(): Product[] {
  /* The map is keyed by every product, so its keys are products. */
  return (Object.keys(CARRIES_TIMES) as Product[]).filter(
    (product) => CARRIES_TIMES[product],
  )
}

/**
 * Whether the org site being served carries the luach. A congregation's own
 * domain answers as a shul's site; on a product's host the org is always that
 * product's, since one asked for on another host is sent to its own.
 */
export function siteCarriesTimes(): boolean {
  const product = currentProduct()
  return product ? CARRIES_TIMES[product] : true
}

/**
 * Which products' org sites offer View my account — a member's statement,
 * membership and what is ongoing, by a link mailed to them.
 *
 * The institutional product's orgs keep member accounts; a charity's site takes
 * gifts and bills nobody, so there is no account to view. The server holds the
 * same answer and refuses the request where this says no.
 */
const OFFERS_ACCOUNT: Record<Product, boolean> = {
  [DEFAULT_PRODUCT]: true,
  terumah: false,
  studio: false,
}

/** Whether the org site being served offers View my account. */
export function siteOffersAccount(): boolean {
  const product = currentProduct()
  return product ? OFFERS_ACCOUNT[product] : true
}

/** What the office calls its org on the site being served (`officeOrgNounFor`). */
export function siteOfficeOrgNoun() {
  return officeOrgNounFor(currentProduct())
}

/** What an org is called on the site being served (`orgWordFor`). */
export function siteOrgWord(): string {
  return orgWordFor(currentProduct())
}

/** Who a gift is to on the site being served (`giftToFor`). */
export function siteGiftTo(orgName: string): string {
  return giftToFor(currentProduct(), orgName)
}

/** The name of whoever answers on this site: a product, or the platform. */
export function siteName(): string {
  return currentSite() === 'platform'
    ? PLATFORM_NAME
    : productName(currentProduct() ?? undefined)
}

/** A site we serve — every one of them ours, so every one has a known origin. */
export type OurSite = Product | 'platform'

/**
 * Where another of our sites answers, as an origin to send somebody to.
 *
 * Built from where THIS page is being served rather than from a constant, so a
 * link between products works in development, in a preview and in production
 * without any of them being configured: the loopback names each site by its
 * own subdomain, and everywhere else names it by its registered host.
 */
export function siteOrigin(site: OurSite): string {
  const here = new URL(window.location.href)
  // The numeric loopback is the same machine, so it names sites the same way;
  // read as anything else, a local page would send somebody to production.
  if (
    here.hostname === 'localhost' ||
    here.hostname === '127.0.0.1' ||
    here.hostname.endsWith('.localhost')
  ) {
    const sub = site === 'platform' ? 'platform' : site
    return `${here.protocol}//${sub}.localhost${here.port ? `:${here.port}` : ''}`
  }
  return `https://${site === 'platform' ? PLATFORM_HOST : hostFor(site)}`
}

/**
 * Which face of a product this host serves.
 *
 * A product answers on two hosts: its own, which is the PUBLIC site and serves
 * anybody the same whether or not they are signed in, and `app.` in front of
 * it, where its sessions live and the back office answers. Null off a product —
 * the platform and a congregation's own domain each have one face.
 */
export type Face = 'public' | 'app'

export function resolveFace(url: URL): Face | null {
  const site = resolveSite(url)
  if (site === 'platform' || site === 'org') return null
  return isAppHost(url.hostname) ? 'app' : 'public'
}

/** The face the browser is currently on. */
export function currentFace(): Face | null {
  return resolveFace(new URL(window.location.href))
}

/**
 * Where a product's face answers, as an origin to send somebody to — built
 * from `siteOrigin`, so it is right in development and production alike.
 */
export function faceOrigin(product: Product, face: Face): string {
  const url = new URL(siteOrigin(product))
  if (face === 'app') url.hostname = appHostFor(url.hostname)
  return url.origin
}

/**
 * Where a site's app answers — the one place a session can be handed to. A
 * product's app is its own host; the platform's app is the platform.
 */
export function appOrigin(site: OurSite): string {
  return site === 'platform' ? siteOrigin('platform') : faceOrigin(site, 'app')
}

/**
 * Whether we are serving the PLATFORM's own address rather than a congregation's.
 *
 * The distinction is not cosmetic: an org's own site must not advertise other
 * orgs, so the masthead's org picker and the directory link both hang off this.
 * Every host of ours counts — a product's as much as the platform's; what is
 * excluded is a domain a congregation owns.
 */
export function isPlatformSurface(): boolean {
  return currentSite() !== 'org'
}

/**
 * Go to a public address without fetching the document again.
 *
 * The public surface is one bundle that already resolves the whole route map in
 * the browser, so following a link inside the site over the network throws away
 * a running app to rebuild the same one — a blank frame between every page of a
 * site whose pages are otherwise instant.
 *
 * A `popstate` is dispatched because `pushState` fires no event of its own,
 * which would leave the address changed with nothing listening. Back and a link
 * then reach `usePublicRoute` through one subscription rather than two paths
 * free to disagree about what the current page is.
 */
export function goPublic(href: string): void {
  const here = `${window.location.pathname}${window.location.search}${window.location.hash}`
  /*
   * The address you are already at is not a journey. Pushed anyway it becomes a
   * history entry identical to the one beneath it, and Back then appears to do
   * nothing — the browser makes this a replace for a real navigation, and a
   * pushState has to be told.
   */
  if (href === here) return

  window.history.pushState(null, '', href)
  window.dispatchEvent(new PopStateEvent('popstate'))
  // A page starts at its top: moving from a long schedule to a short contact
  // page otherwise arrives part-way down it, at whatever offset the last page
  // was left at.
  window.scrollTo(0, 0)
  focusPageStart()
}

/**
 * Put the keyboard at the start of the page that just opened.
 *
 * A document that is fetched does this for nothing — focus returns to the top
 * and the reader is told where they are. Swapping the page in place keeps the
 * focus that was on the link, and the link is often the thing that has just
 * been replaced (the page you are on is stated rather than linked), so focus
 * falls to `<body>` and the next Tab starts the document again.
 *
 * The heading is the target because it is what the page IS. It takes focus
 * without becoming a tab stop, so the reading order is unchanged for everybody
 * who did not just navigate; announcing it is the point rather than a side
 * effect. A frame late, because the page it belongs to has not rendered yet.
 */
function focusPageStart(): void {
  requestAnimationFrame(() => {
    const heading = document.querySelector('h1')
    if (!heading) return
    heading.setAttribute('tabindex', '-1')
    heading.focus({ preventScroll: true })
  })
}

/** The address the browser is at, re-read whenever it changes. */
export function usePublicRoute(): PublicRoute {
  const [route, setRoute] = useState(currentPublicRoute)
  useEffect(() => {
    const reread = () => setRoute(currentPublicRoute())
    window.addEventListener('popstate', reread)
    return () => window.removeEventListener('popstate', reread)
  }, [])
  return route
}
